import { Col, Row, Spin } from "antd";
import hourWork from "../../../styles/images/hourWork.svg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { useNavigate } from "react-router-dom";
import useIsConnected from "../../../utils/IsConnected";
import { motion, useReducedMotion } from "framer-motion";
import { Fragment, memo, useCallback, useEffect, useState } from "react";
import ChooseVehicleModal from "../../../Components/vehicule/ChooseVehicleModal";
import { BsCartPlusFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { setWalletAmount } from "../../../apis/slices/globalSlice";
import { entityIdsEqual } from "../../../apis/slices/utils";
import { buildDetailNavState } from "../../../utils/shopListReturn";
import ServiceThumbnail from "../../../Components/shop/ServiceThumbnail";

interface Props {
  id: number;
  name: string;
  description: string;
  description_website: string;
  img: string;
  price: any;
  category: string;
  discount: number;
  duration: number;
  warranty: number;
  feature_ids: any;
  type: "product" | "service";
  toAddService?: (v: boolean) => void;
  toAddProduct?: () => void;
}
const Item = memo(function Item({
  id,
  name,
  description: _description,
  img,
  price,
  type,
  category: _category,
  discount,
  duration,
  warranty,
  description_website: _descriptionWebsite,
  toAddService,
  toAddProduct: _toAddProduct,
  feature_ids,
}: Props) {
  const { t } = useTranslation();
  const { services, vehicle, browseServicesWithoutVehicle } = useSelector((state: RootState) => state?.global);
  const addShopLoading = useSelector(
    (state: RootState) => state?.services?.AddShop?.loading ?? false
  );
  const isConnected = useIsConnected();
  const navigate = useNavigate();
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [chooseVehicleOpen, setChooseVehicleOpen] = useState(false);
  const [pendingAddAfterVehicle, setPendingAddAfterVehicle] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const prefersReducedMotion = useReducedMotion();

  const toAddToCart = useCallback(() => {
    if (addShopLoading) return;
    if (!isConnected) {
      navigate("/login");
      return;
    }
    if (type === "service" && (browseServicesWithoutVehicle || !vehicle?.id)) {
      setPendingAddAfterVehicle(true);
      setChooseVehicleOpen(true);
      return;
    }
    toAddService?.(
      services.some((el: any) => entityIdsEqual(el?.id, id))
    );
    dispatch(setWalletAmount(0));
  }, [
    addShopLoading,
    dispatch,
    id,
    isConnected,
    navigate,
    services,
    toAddService,
    type,
    vehicle,
    browseServicesWithoutVehicle,
  ]);

  useEffect(() => {
    if (!pendingAddAfterVehicle || !vehicle || browseServicesWithoutVehicle) return;
    setPendingAddAfterVehicle(false);
    toAddToCart();
  }, [pendingAddAfterVehicle, vehicle, browseServicesWithoutVehicle, toAddToCart]);

  const closeChooseVehicleModal = useCallback(() => {
    setChooseVehicleOpen(false);
  }, []);

  const onVehicleChosenForCart = useCallback(() => {
    setPendingAddAfterVehicle(true);
  }, []);

  const handleMoreDetailsClick = () => {
    setIsFlipped(!isFlipped);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleMoreDetailsClick();
    }
  };

  const gradientId = `ac-svc-check-${id}`;
  const serviceFlipHint = (
    <div className="ac-service-card__flip-hint" aria-hidden>
      <div className="ac-service-card__flip-hint-stack">
        <span className="ac-service-card__installments-short">
          <span>{t("services.installmentsShort")}</span>
          <span className="ac-service-card__installment-logos" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL ?? ""}/assets/imgs/bnpl/tabby-logo.png`} alt="" loading="lazy" />
            <img src={`${process.env.PUBLIC_URL ?? ""}/assets/imgs/bnpl/tamara-logo.png`} alt="" loading="lazy" />
          </span>
        </span>
        <span className="ac-service-card__flip-hint-text">
          {t("services.flipHint")}
        </span>
      </div>
    </div>
  );

  const warrantyBadgeOnImage = (
    <div className="ac--garanty-container ac-service-card__warranty-badge ac-service-card__warranty-badge--on-image">
      <span className="ac-service-card__warranty-num">{warranty}</span>
      <span className="ac-service-card__warranty-years">
        {t("services.years")}
      </span>
      <span className="ac-service-card__warranty-label">
        {t("services.garranty")}
      </span>
    </div>
  );

  return (
    <>
      <motion.div
        role="button"
        tabIndex={0}
        aria-expanded={isFlipped}
        aria-label={`${name}. ${t("services.installmentsShort")}. ${t("services.flipHint")}`}
        initial={{ rotateY: 0 }}
        animate={isFlipped ? { rotateY: 180 } : { rotateY: 0 }}
        transition={{ duration: prefersReducedMotion ? 0.12 : 0.5 }}
        style={{ transformStyle: "preserve-3d" }}
        className="ac-service-card ac--service-container ac--pointer card-journey-small background-card hover-up"
        onKeyDown={handleCardKeyDown}
        onClick={(e) => {
          e.stopPropagation();
          handleMoreDetailsClick();
        }}
      >
        <div className="ac-service-card__body">
          {!isFlipped ? (
            <div className="ac-service-card__front">
              {!vehicle && isConnected ? (
                <div className="card-image">
                  <span
                    className="wish"
                    role="button"
                    tabIndex={0}
                    aria-label={t("services.chooseVehicle.openAria")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChooseVehicleOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        setChooseVehicleOpen(true);
                      }
                    }}
                  >
                    <BsCartPlusFill
                      fill={"var(--ac-theme-accent)"}
                      color={"#fff"}
                      size={"20"}
                    />
                  </span>
                  {type === "service" ? (
                    <ServiceThumbnail src={img} alt={name} />
                  ) : img ? (
                    <img src={img} alt={name} />
                  ) : (
                    <img
                      src="assets/imgs/shop/shop-list/product2.png"
                      alt={name}
                    />
                  )}
                  {warrantyBadgeOnImage}
                  {serviceFlipHint}
                </div>
              ) : (
                <div className="card-image">
                  <span
                    className="wish"
                    onClick={(e) => {
                      e.stopPropagation();
                      toAddToCart();
                    }}
                  >
                    {addShopLoading ? (
                      <Spin size="small" />
                    ) : (
                      <BsCartPlusFill
                        fill={"var(--ac-theme-accent)"}
                        color={"#fff"}
                        size={"20"}
                      />
                    )}
                  </span>
                  {type === "service" ? (
                    <ServiceThumbnail src={img} alt={name} />
                  ) : img ? (
                    <img src={img} alt={name} />
                  ) : (
                    <img
                      src="assets/imgs/shop/shop-list/product2.png"
                      alt={name}
                    />
                  )}
                  {warrantyBadgeOnImage}
                  {serviceFlipHint}
                </div>
              )}
              <div className="card-title">
                <Row className="ac--service-title-container" wrap={false}>
                  <Col flex="auto" className="text-center" style={{ minWidth: 0 }}>
                    <p className="ac-service-card__name">{name}</p>
                  </Col>
                </Row>
                <Row className="ac--service-price-container" wrap={false}>
                  <Col flex="none">
                    <img src={hourWork} alt="" />
                  </Col>
                  <Col flex="none" style={{ width: "22%" }}>
                    <div className="ac--service-hour-container ac-service-card__duration">
                      <span className="text-center text-md-bold text-white ac-service-card__duration-value">
                        {duration}
                        <span className="text-sm-bold text-white">
                          {t("services.hr")}
                        </span>
                      </span>
                      <span className="text-xs text-white ac-service-card__duration-label">
                        {t("services.workingHours")}
                      </span>
                    </div>
                  </Col>
                  <Col flex="auto" style={{ minWidth: 0 }}>
                    <div className="card-price">
                      <h6 className="text-sm-medium neutral-500">
                        {(browseServicesWithoutVehicle || !vehicle?.id) && t("services.price")}
                      </h6>
                    </div>
                    <span className="ac-service-card__price text-center text-xl-bold text-white">
                      {isConnected && discount > 0 && discount != price ? (
                        <>
                          {discount}
                          <span className="text-md-bold text-white icon-saudi_riyal" />
                        </>
                      ) : (
                        <>
                          {price}
                          <span className="text-md-bold text-white icon-saudi_riyal" />
                        </>
                      )}
                    </span>
                  </Col>
                  <Col flex="none" style={{ textAlign: "end", width: "26%" }}>
                    {isConnected && discount > 0 && discount != price && (
                      <div className="ac--service-discount-container">
                        <span
                          className="text-center text-sm text-white"
                          style={{ fontSize: "12px" }}
                        >
                          {t("services.discount")}
                        </span>
                        <span className="text-center text-md-bold text-white diagonal-line">
                          {price}
                        </span>
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            </div>
          ) : (
            <div className="ac--card-image-back-side ac-service-card__back">
              <div className="ac--card-image-back-side-title">
                <p className="ac-service-card__back-title">{name}</p>
                <div className="ac--card-image-back-side-logo ac-service-card__warranty-badge-back">
                  <div className="ac--garanty-container-back-side ac-service-card__warranty-back-top">
                    <p className="ac-service-card__warranty-num">
                      {warranty}
                    </p>
                  </div>
                  <div className="ac--garanty-container-back-side-mid ac-service-card__warranty-back-mid">
                    <p className="ac-service-card__warranty-years">
                      {t("services.years")}
                    </p>
                  </div>
                  <div className="ac--garanty-container-back-side ac--garanty-container-back-side-border ac-service-card__warranty-back-bottom">
                    <p className="ac-service-card__warranty-label">
                      {t("services.garranty")}
                    </p>
                  </div>
                </div>
                <div className="ac-service-card__features">
                  {(feature_ids?.length ?? 0) > 0 ? (
                    <p className="ac-service-card__features-heading">
                      {t("services.includedHeading")}
                    </p>
                  ) : null}
                  <Row align="middle" gutter={[0, 8]}>
                    {(feature_ids ?? []).map((item: any, index: number) => (
                      <Fragment key={item?.id ?? index}>
                        <Col
                          span={2}
                          offset={1}
                          className="ac-service-card__feature-check"
                        >
                          <svg
                            className="ac-service-card__check-svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden
                          >
                            <rect
                              x="1"
                              y="1"
                              width="16"
                              height="16"
                              rx="2"
                              stroke={`url(#${gradientId})`}
                              strokeWidth="2"
                            />
                            <path
                              d="M7.1375 13C7.05526 12.9995 6.97393 12.9815 6.89821 12.9472C6.82249 12.9129 6.75386 12.863 6.69629 12.8002L4.20362 10.0943C4.14097 10.0333 4.0906 9.95936 4.05561 9.87688C4.02062 9.7944 4.00174 9.70517 4.00011 9.61462C3.99849 9.52408 4.01416 9.43414 4.04617 9.35029C4.07818 9.26644 4.12586 9.19045 4.18628 9.12696C4.24671 9.06347 4.31861 9.01382 4.3976 8.98104C4.47659 8.94826 4.56101 8.93304 4.64569 8.93632C4.73037 8.93959 4.81354 8.96129 4.89011 9.00008C4.96668 9.03887 5.03504 9.09394 5.09101 9.16193L7.14061 11.3883L12.9286 5.20332C12.9861 5.13971 13.0548 5.08897 13.1309 5.05407C13.2069 5.01917 13.2887 5.00079 13.3714 5.00003C13.4542 4.99926 13.5362 5.01611 13.6128 5.04959C13.6894 5.08308 13.759 5.13253 13.8175 5.19506C13.876 5.25759 13.9223 5.33195 13.9536 5.4138C13.9849 5.49565 14.0007 5.58334 14 5.67177C13.9993 5.7602 13.9821 5.84759 13.9494 5.92884C13.9167 6.0101 13.8693 6.08358 13.8098 6.14502L7.57808 12.8049C7.46124 12.9298 7.30276 13 7.1375 13Z"
                              fill="#F4F5F7"
                            />
                            <defs>
                              <linearGradient
                                id={gradientId}
                                x1="0"
                                y1="9"
                                x2="18"
                                y2="9"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#5D2765" />
                                <stop offset="1" stopColor="#912264" />
                              </linearGradient>
                            </defs>
                          </svg>
                        </Col>
                        <Col span={20}>
                          <p className="text-md-bold text-white">{item.name}</p>
                        </Col>
                      </Fragment>
                    ))}
                  </Row>
                </div>
              </div>

              {type === "service" ? (
                <ServiceThumbnail src={img} alt={name} />
              ) : img ? (
                <img src={img} alt="" />
              ) : (
                <img
                  src="assets/imgs/shop/shop-list/product2.png"
                  alt=""
                />
              )}
            </div>
          )}
        </div>

        <div
          role="button"
          tabIndex={0}
          aria-label={t("services.moreDetails")}
          className="ac-service-card__footer ac--more-details-container"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              navigate(
                "/detail",
                {
                  state: buildDetailNavState({
                    id,
                    type,
                    returnTo:
                      type === "service" ? "/services" : "/products",
                  }),
                }
              );
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              "/detail",
              {
                state: buildDetailNavState({
                  id,
                  type,
                  returnTo: type === "service" ? "/services" : "/products",
                }),
              }
            );
          }}
        >
          <span className={isFlipped ? "ac--flipped-title" : ""}>
            {t("services.moreDetails")}
          </span>
        </div>
      </motion.div>
      <ChooseVehicleModal
        open={chooseVehicleOpen}
        onClose={closeChooseVehicleModal}
        onVehicleSelected={onVehicleChosenForCart}
      />
    </>
  );
});

export default Item;
